"use client"

import type { Flow } from "@/modules/jobAds/client/components/SavedSearchConfirmationModal/SavedSearchConfirmationModal"
import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { BellOutlined } from "@jobteaser/spark/components/Icons/BellOutlined"

import { SEARCH_ENGINE_VERSIONS } from "@/modules/jobAds/client/constants"
import { useAddUserSavedSearch } from "@/modules/jobAds/client/hooks/useAddUserSavedSearch"
import { useJobAdsClientTracking } from "@/modules/jobAds/client/hooks/useJobAdsClientTracking"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { useSavedSearchesButton } from "@/modules/jobAds/client/hooks/useSavedSearchesButton"
import {
  areSearchParamsEmpty,
  computeSearchParamsFromURLSearchParams,
} from "@/modules/jobAds/client/utils/computeSearchParamsFromURLSearchParams"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { useNotification } from "@/modules/notification/hooks"

const DynamicSavedSearchConfirmationModal = dynamic(() =>
  import("@/modules/jobAds/client/components/SavedSearchConfirmationModal/SavedSearchConfirmationModal").then(
    mod => mod.SavedSearchConfirmationModal
  )
)

type SavedSearchesButtonProps = {
  alreadySavedLabel: string
  className?: string
  errorMessage: string
  errorTitle: string
  saveLabel: string
}

export const SavedSearchesButton: FunctionComponent<SavedSearchesButtonProps> = ({
  alreadySavedLabel,
  className,
  errorMessage,
  errorTitle,
  saveLabel,
}) => {
  const searchParams = useJobAdsSearchParams()
  const { savedSearch } = usePageContext()
  const { trackSavedSearchCreated } = useJobAdsClientTracking()
  const { isCurrentSearchDifferentFromSavedSearch } = useSavedSearchesButton()

  const showNotification = useNotification()
  const { addUserSavedSearch, isLoading } = useAddUserSavedSearch()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [savedSearchConfirmationModalFlow, setSavedSearchConfirmationModalFlow] = useState<Flow>("create")

  const computedSearchParams = computeSearchParamsFromURLSearchParams(searchParams)

  const saveCurrentSearch = ({ onError, onSuccess }: { onError?: () => void; onSuccess?: () => void } = {}): void => {
    addUserSavedSearch({
      ...computedSearchParams,
      searchEngineVersion: SEARCH_ENGINE_VERSIONS.ALGOLIA,
    })
      .then(() => {
        onSuccess?.()
      })
      .catch(() => {
        showNotification({
          content: errorMessage,
          title: errorTitle,
          variant: "error",
        })
        onError?.()
      })
  }

  const handleSaveClick = (): void => {
    if (areSearchParamsEmpty(computedSearchParams)) {
      return
    }

    trackSavedSearchCreated(computedSearchParams, { hasAlreadySavedSearch: !!savedSearch })
    setSavedSearchConfirmationModalFlow(savedSearch ? "replace" : "create")
    setIsModalOpen(true)
  }

  const handleModalClose = (): void => {
    setIsModalOpen(false)
  }

  return (
    <>
      <Button
        className={className}
        data-testid="saved-search-button"
        disabled={isLoading || !isCurrentSearchDifferentFromSavedSearch}
        icon={BellOutlined}
        onClick={handleSaveClick}
        variant="highlight"
      >
        {isCurrentSearchDifferentFromSavedSearch ? saveLabel : alreadySavedLabel}
      </Button>
      {isModalOpen && (
        <DynamicSavedSearchConfirmationModal
          existingSavedSearchSearchParams={savedSearch?.searchParams}
          flow={savedSearchConfirmationModalFlow}
          onClose={handleModalClose}
          onConfirm={saveCurrentSearch}
          searchToSavedSearchParams={computedSearchParams}
        />
      )}
    </>
  )
}
